import { useTranslation } from "react-i18next";

import { LoadingButton } from "@mui/lab";
import { Button, Typography, styled } from "@mui/material";
import { password } from "marvin-auth-kit";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { TextField } from "formik-mui";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useConfig } from "../../hooks/settingHooks";
import { useErrorSnackbar } from "../../hooks/errorHooks";
import { NavLink } from "../../components/navigation/NavLink";

interface Props {}

interface ForgotPasswordForm {
  username: string;
}

export function PasswordForgotPage(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const config = useConfig();
  const initialValues: ForgotPasswordForm = { username: "" };
  const validationScheme = Yup.object().shape({
    username: Yup.string().required(
      t("validation.requiredField", { field: t("label.userName") })
    ),
  });

  async function onSubmit(
    values: ForgotPasswordForm,
    helpers: FormikHelpers<ForgotPasswordForm>
  ) {
    const { setSubmitting } = helpers;
    const { username } = values;
    setSubmitting(true);
    try {
      await password.request(username);
      enqueueSnackbar(t("message.emailHasBeenSent", { email: username }), {
        variant: "success",
      });
      navigate(`/${config}/app/login`);
    } catch (errors) {
      enqueueErrorSnackbar(errors);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationScheme}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <StyledForm>
          <Typography variant="h3" component="h3" color="textSecondary">
            {t("label.forgotPassword")}
          </Typography>
          <Typography
            variant="subtitle1"
            component="h4"
            color="textSecondary"
            className="subtitle"
          >
            {t("label.enterYourUsernameToContiue")}
          </Typography>
          <Field
            component={TextField}
            autoFocus
            className="login-field"
            name="username"
            type="text"
            label={t("label.userName")}
            variant="filled"
            disabled={isSubmitting}
          />
          <div className="button-group">
            <LoadingButton
              className="submit"
              variant="contained"
              loading={isSubmitting}
              type="submit"
            >
              {t("label.reset")}
            </LoadingButton>
            <Button
              component={NavLink}
              to={`/${config}/app/login`}
              variant="text"
              disabled={isSubmitting}
            >
              {t("label.cancel")}
            </Button>
          </div>
        </StyledForm>
      )}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  .button-group {
    > * + * {
      margin-left: ${({ theme }) => `${theme.spacing(1)}`};
    }
  }

  .login-field,
  .subtitle,
  .button-group {
    margin-bottom: ${({ theme }) => `${theme.spacing(3)}`};
  }
`;
